"use client";

import Link from "next/link";
import TeacherAvatar from "./TeacherAvatar";
import { Globe, Languages } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { getRegionLocaleHref } from "@/services/region";

export type UserCardItem = {
  id: string | number;
  image: string;
  name: string;
  // The canonical teacher profile URL - never the numeric id (see
  // profileHref below).
  slug: string;
  date: string;
  country: string;
  languages?: Array<{ id: number; code: string; name: string }>;
};

/** Beyond this the chips wrap to a third line and unbalance the grid. */
const MAX_VISIBLE_LANGUAGES = 3;

type UserCardProps = {
  user: UserCardItem;
};

export default function UserCard({ user }: UserCardProps) {
  const locale = useLocale();
  const t = useTranslations("web.teachers");
  const pathname = usePathname();
  const profileHref = getRegionLocaleHref(pathname, locale, `/teachers/${user.slug}`);
  // Deduplicated by code: the payload can repeat a language across the primary
  // field and the array, which would otherwise render two identical chips.
  const languages = dedupeLanguages(user.languages ?? []);

  return (
    // Flex column so the button can pin to the bottom: names run to four or
    // five words and wrap rather than truncating, so cards in a row no longer
    // have the same content height. Flat white with the depth carried by the
    // shadow, and tight padding so the image runs close to the card edge.
    <div className="flex h-full flex-col bg-white rounded-2xl p-3 shadow-lg hover:shadow-xl transition-shadow duration-300">
      {/* A teacher with a photo gets the full-bleed photo; one without gets
          their initials on a tint keyed to their name, rather than the same
          generic person icon as everybody else.
          `aspect-square` rather than a fixed `h-65`: the well is full-width, so
          a fixed height made it a rectangle whose proportions drifted with the
          column - narrow and tall at one breakpoint, wide and short at another. */}
      <div className="w-full aspect-square relative mb-3 rounded-xl overflow-hidden shrink-0">
        <TeacherAvatar src={user.image} name={user.name} shape="fill" />
      </div>

      {/* Text and button keep a little inset of their own - the card padding is
          now tight enough for the image, but type sitting that close to the
          edge reads as cramped. */}
      <div className="flex flex-1 flex-col px-2 pb-1">

      {/* Name shown in full: `truncate` used to cut most of the roster off at
          the card edge ("Abdullah Ragab Mohamed B..."), which is the one thing
          a teacher card has to get across. `break-words` keeps a single long
          token from overflowing, and the min-height reserves the second line so
          single-line names don't sit tighter than the rest. */}
      <h3 className="!font-semibold !text-xl mb-2 min-h-[2.6em] leading-snug break-words !text-primary">
        {user.name}
      </h3>

      {/* Info */}
      <div className="flex items-center gap-10 text-[#2D3748] text-sm mb-3">
        <span className="flex items-center gap-1">
          <Globe className="w-4 h-4 text-secondary" />
          {user.country}
        </span>
      </div>

      {/* Which languages the teacher instructs in. Capped so a polyglot doesn't
          stretch the card past its neighbours; the rest are counted in a "+N"
          chip whose title lists them. */}
      {languages.length > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {languages.slice(0, MAX_VISIBLE_LANGUAGES).map((language) => (
            <span
              key={language.id || language.code || language.name}
              className="inline-flex items-center gap-1 rounded-[10px] bg-secondary/10 px-2.5 py-1 text-xs font-semibold text-[#0D6C90]"
            >
              <Languages className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {language.name}
            </span>
          ))}

          {languages.length > MAX_VISIBLE_LANGUAGES ? (
            <span
              title={languages
                .slice(MAX_VISIBLE_LANGUAGES)
                .map((language) => language.name)
                .join(", ")}
              className="inline-flex items-center rounded-[10px] bg-slate-100 px-2 py-1 text-xs font-bold text-slate-500"
            >
              +{languages.length - MAX_VISIBLE_LANGUAGES}
            </span>
          ) : null}
        </div>
      ) : null}

        {/* `mt-auto` keeps the button on the card's bottom edge whether the
            name above took one line or three. */}
        <Link
          href={profileHref}
          className="mt-auto block w-full bg-primary hover:bg-secondary !rounded-[10px] text-center text-white py-2 transition cursor-pointer"
        >
          {t("view_profile")} →
        </Link>
      </div>
    </div>
  );
}

function dedupeLanguages(
  languages: NonNullable<UserCardItem["languages"]>,
): NonNullable<UserCardItem["languages"]> {
  const seen = new Set<string>();

  return languages.filter((language) => {
    const key = (language.code || language.name).trim().toLowerCase();

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);

    return true;
  });
}
